import React, {
  createContext,
  useContext,
  useMemo,
  useRef,
  MutableRefObject,
} from "react";
import {
  GetEffectiveExpenseRateSheet,
  GetEffectiveTaskRateSheet,
  GetEmployeeRate,
  GetPerDiemRate,
  RateSheetDependencies,
} from "../types";
import { TaskRate, ChargeType } from "~/gql/types";
import moment from "moment";
import { useChargeFormGraphqlApi } from "~/project-charge-form/chargeFormProviders/ChargeFormGraphqlApi";
import { useFinalFormBoundary } from "./FinalFormBoundary";
import { ExpenseRate } from "~/gql/ExpenseRate";
import { rateAmountMatch } from "../functions/rateAmountMatch";

type RateSheetContextData = {
  rateValidationMessage: MutableRefObject<string | null>;
  rateAmountValidationMessage: MutableRefObject<string | null>;
  getEffectiveExpenseRateSheet: GetEffectiveExpenseRateSheet;
  getEffectiveTaskRateSheet: GetEffectiveTaskRateSheet;
  getPerDiemRate: GetPerDiemRate;
  getEmployeeRate: GetEmployeeRate;
  clearRateField: () => void;
  changeRateAmountField: (newRateAmount: string) => void;
};

const RateSheetContext = createContext<RateSheetContextData | null>(null);

/**
 * Mediates between rate sheet data and the form: resolves the effective rates,
 * checks the current rate against them, and asks `FinalFormBoundary` to correct
 * fields. See the `charge-form-editor-providers` spec.
 */
export const RateSheetProvider: React.FC = ({ children }) => {
  const {
    getEffectiveExpenseRateSheet,
    getEffectiveTaskRateSheet,
    getPerDiemRate,
    getEmployeeRate,
  } = useChargeFormGraphqlApi();

  const { clearRateField, changeRateAmountField } = useFinalFormBoundary();

  const rateValidationMessage = useRef(null);
  const rateAmountValidationMessage = useRef(null);

  const rateSheetContextvalue = useMemo(
    () => ({
      rateValidationMessage,
      rateAmountValidationMessage,
      getEffectiveExpenseRateSheet,
      getEffectiveTaskRateSheet,
      getPerDiemRate,
      getEmployeeRate,
      clearRateField,
      changeRateAmountField,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rateValidationMessage, rateAmountValidationMessage]
  );

  return (
    <RateSheetContext.Provider value={rateSheetContextvalue}>
      {children}
    </RateSheetContext.Provider>
  );
};

const useRateSheetContext = (fn: string): RateSheetContextData => {
  const context = useContext(RateSheetContext);

  if (!context) {
    throw new Error(`Cannot call ${fn} outside of a RateSheetProvider wrapper`);
  }

  return context;
};

type RateSheetApi = {
  rateValidationMessage: MutableRefObject<string | null>;
  rateAmountValidationMessage: MutableRefObject<string | null>;
  handleRateSuggestions: (
    props: RateSheetDependencies,
    rateInputsModified: boolean
  ) => Promise<ExpenseRate[] | TaskRate[] | null>;
};

export function useRateSheet(): RateSheetApi {
  const {
    rateValidationMessage,
    rateAmountValidationMessage,
    getEffectiveExpenseRateSheet,
    getEffectiveTaskRateSheet,
    getPerDiemRate,
    getEmployeeRate,
    clearRateField,
    changeRateAmountField,
  } = useRateSheetContext("useRateSheet");

  return {
    rateValidationMessage,
    rateAmountValidationMessage,
    /**
     * runs every time a form value that affects the rate selection is changed
     */
    handleRateSuggestions: async (
      props: RateSheetDependencies,
      rateInputsModified: boolean
    ) => {
      const { date, project, type, rate, rateAmount, useCustomRate, employee } =
        props;

      // skip if there's no project or date selected, or if we're using a custom rate
      if (!project || !date || useCustomRate) {
        return null;
      }

      // get the effective (usable) expense or task rate sheet ("rate suggestions") for the date and project selected
      const asOf = moment(date).format("YYYY-MM-DD");

      const response = await (type === ChargeType.Task
        ? getEffectiveTaskRateSheet(asOf, project.number)
        : type === ChargeType.Expense
        ? getEffectiveExpenseRateSheet(asOf, project.number)
        : []);

      const rateSuggestions = response && response.length > 0 ? response : null;

      // nothing to mutate when no rate is selected, or when no rate input has been modified
      // (the loaded charge's rate and rate amount stand until the user changes one)
      if (!rate || !rateInputsModified) {
        return rateSuggestions;
      }

      // check if the selected rate value exists in the updated rate sheet
      const matchingRate = rateSuggestions?.find(
        (x: ExpenseRate | TaskRate) => x.name === rate.name
      );

      // clear the rate amount field if the selected value does not exist in the updated rate sheet
      if (!matchingRate) {
        rateValidationMessage.current = `No ${rate.name} rate found for project ${project.number} and date ${asOf}.`;

        clearRateField();
      } else {
        rateValidationMessage.current = null;

        // update the rate amount field based on the matching rate's unique rate amount
        const newRateAmount = await rateAmountMatch({
          matchingRate,
          rateAmountFieldValue: rateAmount,
          asOf,
          project,
          employee,
          getPerDiemRate,
          getEmployeeRate,
        });

        if (newRateAmount) {
          rateAmountValidationMessage.current = "Rate amount updated";
          changeRateAmountField(newRateAmount);
        }
      }

      return rateSuggestions;
    },
  };
}
